import { useApolloClient } from '@vue/apollo-composable';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { MeDocument } from '@teamango/graphql';
import type { CurrentUser } from '@/shared/types';

export const useAuthStore = defineStore('auth', () => {
  const { client } = useApolloClient();

  const user = ref<CurrentUser | null>();

  const isMeLoading = ref(false);

  // True while a logout flow initiated on this tab is running. Used to mute
  // the cross-tab SESSION_LOGGED_OUT subscription handler on the same tab so
  // it doesn't race with useLogout's own teardown.
  const isLoggingOut = ref(false);

  const fetchMe = async () => {
    isMeLoading.value = true;

    try {
      const result = await client.query({
        query: MeDocument,
        fetchPolicy: 'network-only',
      });

      if (!result.data?.me) {
        return;
      }

      user.value = (result.data?.me) ?? null;
    } finally {
      isMeLoading.value = false;
    }
  };

  // Note: `user` is intentionally NOT cleared here. Setting `user` to `null`
  // while MainLayout is still mounted crashes its template (which dereferences
  // currentUser). To transition to the "logged-out" state, refetch via
  // `fetchMe` — the API returns the Guest user once the session cookie is gone.
  const $reset = () => {
    isMeLoading.value = false;
  };

  return {
    user,
    isMeLoading,
    isLoggingOut,
    fetchMe,
    $reset,
  };
});
